import { useAppKitNetwork } from '@reown/appkit/react';
import { useCallback } from 'react';
import { useAccount, useChainId, useSwitchChain } from 'wagmi';

import { anchorChain, type AnchorChainId } from '../config/anchors';
import { normalizeChainId, supportedWalletNetworks } from '../config/networks';

import { useAnchorChain } from './useAnchorChain';

export function useWalletNetwork() {
    const { chainId, caipNetworkId, switchNetwork } = useAppKitNetwork();
    const { chainId: accountChainId } = useAccount();
    const wagmiChainId = useChainId();
    const { switchChainAsync } = useSwitchChain();
    const { anchorChainId } = useAnchorChain();

    // the connector's live chain first: AppKit's active network and wagmi's
    // config chain are persisted, so right after a reconnect both can lag the
    // wallet, and a switch prompt would then target the chain it already sits on.
    const normalizedChainId =
        accountChainId ?? normalizeChainId(chainId) ?? wagmiChainId;
    const isOnAnchor = normalizedChainId === anchorChainId;

    /**
     * Put the connected wallet on `targetChainId`, AppKit first and wagmi as the
     * fallback (a plain injected connector answers one or the other depending on
     * the wallet). Resolves false rather than throwing: every caller has its own
     * sentence to say about a wallet that would not move.
     */
    const ensureChain = useCallback(
        async (targetChainId: number): Promise<boolean> => {
            if (normalizedChainId === targetChainId) {
                return true;
            }

            const network = supportedWalletNetworks.find(
                (candidate) => candidate.id === targetChainId,
            );
            try {
                if (!network) {
                    throw new Error(`Unsupported network ${targetChainId}`);
                }
                await switchNetwork(network);
                return true;
            } catch (appKitSwitchError) {
                try {
                    await switchChainAsync({ chainId: targetChainId });
                    return true;
                } catch (wagmiSwitchError) {
                    console.warn('Failed to switch the wallet network', {
                        targetChainId,
                        chainId,
                        caipNetworkId,
                        wagmiChainId,
                        appKitSwitchError,
                        wagmiSwitchError,
                    });
                    return false;
                }
            }
        },
        [
            caipNetworkId,
            chainId,
            normalizedChainId,
            switchChainAsync,
            switchNetwork,
            wagmiChainId,
        ],
    );

    /** Put the wallet on this session's authorization anchor. */
    const ensureAnchor = useCallback(
        async (target: AnchorChainId = anchorChainId): Promise<boolean> =>
            ensureChain(anchorChain(target).chainId),
        [anchorChainId, ensureChain],
    );

    return {
        chainId: normalizedChainId,
        caipNetworkId,
        anchorChainId,
        isOnAnchor,
        ensureChain,
        ensureAnchor,
    };
}
